// The landing page: select a file, upload it, and show the review link.

import type { UploadResult } from "@feedmark/shared/api";
import { showAccount } from "./account";
import { track } from "./analytics";
import { client } from "./api";
import { $, errorText } from "./dom";
import "./own-embed";
import { alertText, startTurnstile } from "./turnstile";

const MAX_BYTES = 5 * 1024 * 1024;
const card = $("card");
const drop = $("drop");
const input = $<HTMLInputElement>("file");
const uploadButton = $<HTMLButtonElement>("upload");
let file: File | null = null;

type State = "idle" | "selected" | "uploading" | "result";

function setState(state: State) {
  card.dataset.state = state;
  drop.hidden = state === "uploading" || state === "result";
  $("progress").hidden = state !== "uploading";
  $("result").hidden = state !== "result";
  uploadButton.hidden = state === "result";
  uploadButton.disabled = state !== "selected";
  uploadButton.textContent = state === "uploading" ? "Uploading…" : "Upload and get review link";
}

function setAlert(text: string) {
  $("alert-text").textContent = text;
  $("alert").hidden = !text;
}

function selectFile(next: File | undefined) {
  if (!next) return;
  if (next.size > MAX_BYTES) {
    file = null;
    $("drop-title").textContent = "Drop your HTML prototype here";
    $("drop-hint").textContent = "or click to select a file";
    setState("idle");
    setAlert("This file is larger than 5 MB. Select a smaller file.");
    return;
  }
  file = next;
  $("drop-title").textContent = next.name;
  $("drop-hint").textContent = `${Math.max(1, Math.round(next.size / 1024))} KB · ready to upload`;
  setAlert("");
  setState("selected");
}

input.addEventListener("change", () => selectFile(input.files?.[0]));
for (const type of ["dragenter", "dragover"]) {
  drop.addEventListener(type, (event) => {
    event.preventDefault();
    drop.classList.add("over");
  });
}
for (const type of ["dragleave", "drop"]) {
  drop.addEventListener(type, (event) => {
    event.preventDefault();
    drop.classList.remove("over");
  });
}
drop.addEventListener("drop", (event) => selectFile(event.dataTransfer?.files[0]));
// A file that is dropped out of the drop zone must not replace the page.
addEventListener("dragover", (event) => event.preventDefault());
addEventListener("drop", (event) => event.preventDefault());

if (matchMedia("(pointer: coarse)").matches) $("drop-hint").textContent = "or tap to select a file";

const getToken = startTurnstile(
  client.api.config
    .$get()
    .then((response) => response.json())
    .then((config) => config.turnstileSiteKey),
);

const CLAIM = "feedmark-claim";
let loggedIn = false;
let result: UploadResult | null = null;

function showResult(data: UploadResult) {
  result = data;
  $<HTMLAnchorElement>("preview").href = data.reviewUrl;
  $<HTMLInputElement>("review-url").value = data.reviewUrl;
  $("owner-yes").hidden = !data.hasOwner;
  // A user who is logged in does not get the login link.
  $("owner-no").hidden = data.hasOwner || loggedIn;
  $("expiry").textContent = new Date(data.expiresAt).toLocaleDateString(undefined, { dateStyle: "long" });
  setState("result");
}

// After the Google login: give the prototype of this tab to the account.
async function claimPrototype(user: { name: string } | null) {
  loggedIn = user !== null;
  let data: UploadResult | null = null;
  try {
    data = JSON.parse(sessionStorage.getItem(CLAIM) ?? "null");
    // The claim data is for one return from the login. A reload must show the drop zone again.
    sessionStorage.removeItem(CLAIM);
  } catch {}
  if (!data) return;
  if (user) {
    const response = await client.api.prototypes[":id"].claim.$post({
      param: { id: data.id },
      json: { claimKey: data.claimKey ?? "" },
    });
    if (response.ok) {
      data.hasOwner = true;
    } else {
      setAlert(await errorText(response, "The login operated, but the prototype has no owner."));
    }
  }
  showResult(data);
}

uploadButton.addEventListener("click", async () => {
  if (!file) return;
  setAlert("");
  $("progress-file").textContent = file.name;
  setState("uploading");
  try {
    const response = await client.api.upload.$post({ form: { file, "cf-turnstile-response": await getToken() } });
    if (!response.ok) {
      // A rate limit rule of Cloudflare can answer 429 before the Worker.
      const status: number = response.status;
      const fallback = status === 429 ? "Too many uploads. Try again in some minutes." : "The upload failed.";
      throw new Error(await errorText(response, fallback));
    }
    const data = await response.json();
    track("prototype_uploaded", { prototype_id: data.id, bytes: file.size, has_owner: data.hasOwner });
    showResult(data);
    $<HTMLInputElement>("review-url").select();
  } catch (error) {
    setState("selected");
    setAlert(alertText(error));
  }
});

// The claim data must stay through the Google login, which leaves this page.
$("claim-login").addEventListener("click", () => {
  if (!result?.claimKey) return;
  try {
    sessionStorage.setItem(CLAIM, JSON.stringify(result));
  } catch {}
});

$("upload-new").addEventListener("click", () => {
  result = null;
  file = null;
  input.value = "";
  $("drop-title").textContent = "Drop your HTML prototype here";
  $("drop-hint").textContent = matchMedia("(pointer: coarse)").matches
    ? "or tap to select a file"
    : "or click to select a file";
  setAlert("");
  setState("idle");
});

const copyButton = $("copy");
copyButton.addEventListener("click", async () => {
  await navigator.clipboard.writeText($<HTMLInputElement>("review-url").value);
  copyButton.textContent = "Copied";
  setTimeout(() => {
    copyButton.textContent = "Copy review link";
  }, 1500);
});

showAccount().then(claimPrototype);
